import {
  AnalyticsService,
  AnalyticsTrackingEvent,
  BuyBoxType,
  isProductOutOfStock,
  PriceDialogContent,
  ProductPrice,
  ProductRewardSearchResult,
} from "@brandclub/common-ui";
import { useTheme, styled } from "@mui/material";
import { useLocationTrackingParams } from "../../../providers/LocationTrackingContextProvider";
import DialogContainer from "../DialogContainer";
import { create } from "zustand";
import "./styles.css";
import { BrandclubPageType } from "@brandclub/types";

import { CustomThemeKeys } from "../../../theme";
import BagDrawer from "../../../modules/BagButton/BagDrawer";
import { useDomainConfig } from "@/api/cache/appConfigQueryOptions";
import { useCartUpdates } from "@/api/cache/cartQueryOptions";
interface DialogState {
  product?: ProductRewardSearchResult;
  open: boolean;
  set: (state: Partial<DialogState>) => void;
  close?: () => void;
}

const useDialogStore = create<DialogState>((set) => ({
  product: undefined,
  open: false,
  set,
}));

/**
 * This is to allow using `sx` prop on the `PriceDialogContent` component
 */
const StyledPriceDialogContent = styled(PriceDialogContent)(() => ({}));

const BuyBoxDialog = () => {
  const { data: domainConfig } = useDomainConfig();
  const [trackingParams] = useLocationTrackingParams();
  const state = useDialogStore();
  const theme = useTheme();
  const { mutate: updateCart } = useCartUpdates();
  const override =
    theme.componentStyleOverrides?.[CustomThemeKeys.ADD_TO_BAG_MODAL_BUTTON];
  const dialogThemeOverride =
    theme.componentStyleOverrides?.[CustomThemeKeys.DIALOG];
  const customBagButtonStyle = {
    textColor: override?.primary || theme.palette.primary?.main,
    bgColor: override?.secondary || theme.palette.secondary?.main,
    borderColor: override?.tertiary || theme.palette.tertiary?.main,
    borderEnabled: override?.borderEnabled,
  };

  const { product, open, close, set } = state;
  let productsToList: ProductRewardSearchResult[] = product
    ? [
        {
          ...product,
        },
      ]
    : [];

  product?.matchedProducts?.forEach((matchedProduct: any) => {
    productsToList.push({
      ...matchedProduct,
    });
  });

  const handleBuyBoxClose = () => {
    set({ open: false });
    close?.();
  };

  const trackBuyBoxClick = async ({
    clickType,
    retailerId,
    retailPrice,
    retailerSku,
    stacklineSku,
    retailerProductPageUrl,
    title,
    availabilityStatus,
  }: {
    clickType: "addToCart" | "view";
    retailerId: number;
    retailPrice: number;
    retailerSku: string;
    stacklineSku: string;
    retailerProductPageUrl: string;
    title: string;
    availabilityStatus?: string;
  }) => {
    const widgetParams = {
      widgetContext: "retailerPrices",
      pageType: BrandclubPageType.Product,
      pageId: stacklineSku,
      retailerUrl: retailerProductPageUrl,
      retailerId,
      retailerSku,
      stacklineSku,
      retailPrice,
      title,
      availabilityStatus,
    };
    const trackingEventAction =
      clickType === "view" ? "openRetailerProductPage" : "addToCart";
    const eventName =
      clickType === "view"
        ? AnalyticsTrackingEvent.WEB_APP_ORGANIC_OPEN_RETAILER_PRODUCT_PAGE
        : AnalyticsTrackingEvent.WEB_APP_ORGANIC_ADD_TO_CART;
    window?.dispatchEvent?.(
      new CustomEvent("bc-track", {
        bubbles: true,
        detail: {
          eventName: trackingEventAction,
          ...trackingParams,
          ...widgetParams,
        },
      })
    );
    await AnalyticsService.track(eventName, {
      organicType: trackingEventAction,
      ...trackingParams,
      ...widgetParams,
    });
  };

  const handlePurchaseRewardPopupOpen = (item: any) => {
    const isOutOfStock = isProductOutOfStock(item);
    trackBuyBoxClick({
      clickType: "view",
      retailerId: item?.retailerId,
      retailPrice: item?.retailPrice,
      retailerSku: item?.retailerSku,
      stacklineSku: item?.stacklineSku,
      retailerProductPageUrl: item?.retailerProductPageUrl,
      title: item?.title,
      availabilityStatus: isOutOfStock ? "OutOfStock" : "InStock",
    });
    window.open(item.retailerProductPageUrl, "_blank");
  };

  const handleBuyClick = (item: any) => {
    const price = item.retailPrice;
    const stacklineSku = item.stacklineSku;
    const retailerSku = item.retailerSku;
    const isOutOfStock = isProductOutOfStock(item);
    trackBuyBoxClick({
      clickType: "addToCart",
      retailerId: item?.retailerId,
      retailPrice: item?.retailPrice,
      retailerSku: item?.retailerSku,
      stacklineSku: item?.stacklineSku,
      retailerProductPageUrl: item?.retailerProductPageUrl,
      title: item?.title,
      availabilityStatus: isOutOfStock ? "OutOfStock" : "InStock",
    });
    updateCart({
      action: "add",
      items: [{ ...item, quantity: 1 }],
      retailerId: item.retailerId,
    });
    BagDrawer?.show();
  };

  if (!open) {
    return null;
  }
  return (
    <DialogContainer close={handleBuyBoxClose}>
      {/* // desktop below */}
      <div
        style={{
          backgroundColor: dialogThemeOverride?.backgroundColor,
        }}
        className="bc-z-10 dtc-modal !bc-hidden tablet:!bc-flex bc-bg-white bc-w-full bc-max-w-[625px] bc-rounded-[6px] bc-text-left bc-overflow-hidden bc-shadow-xl bc-transform bc-transition-all sm:bc-max-w-lg sm:bc-w-full"
      >
        <StyledPriceDialogContent
          sx={{
            color: dialogThemeOverride?.textColor,
            backgroundColor: dialogThemeOverride?.backgroundColor,
          }}
          onClose={handleBuyBoxClose}
          productsToList={productsToList}
          onBuyClick={handleBuyClick}
          onOpenRetailerProductPageClick={handlePurchaseRewardPopupOpen}
          customBagButtonStyle={customBagButtonStyle}
          checkoutAllowedOfferType={domainConfig?.checkoutAllowedOfferType}
        />
      </div>

      {/* // mobile below */}
      <div
        style={{
          backgroundColor: dialogThemeOverride?.backgroundColor,
        }}
        className="!bc-flex tablet:!bc-hidden bc-absolute bc-bottom-[0] bc-w-full bc-max-w-[625px] bc-rounded-t-2xl bc-text-left bc-overflow-hidden bc-shadow-xl bc-transform bc-transition-all sm:bc-max-w-lg sm:bc-w-full"
      >
        <ProductPrice
          buyBoxType={BuyBoxType.BuyBoxBottomSheetAlwaysExpandedWithoutBackdrop}
          productsToList={productsToList}
          onBuyClick={(item: any) => {
            handleBuyClick(item);
            if (item) {
              handleBuyBoxClose();
            }
          }}
          onExpandBuyBoxToggle={handleBuyBoxClose}
          onOpenRetailerProductPageClick={handlePurchaseRewardPopupOpen}
          customBagButtonStyle={customBagButtonStyle}
          checkoutAllowedOfferType={domainConfig?.checkoutAllowedOfferType}
        />
      </div>
    </DialogContainer>
  );
};

BuyBoxDialog.show = (
  product?: ProductRewardSearchResult,
  close?: () => void
) => {
  useDialogStore.setState({
    product,
    open: true,
    close,
  });
};

export default BuyBoxDialog;
